/* app/bi-modals.jsx - BI core modal set.
   Every flow here is wired: manual entry and fetch-from-URL to /api/bi/entities
   and /api/bi/extract-from-url, Grow-from to /api/bi/cross-fetch.
   Built with bi-* class families. */
import { api } from '../lib/api.js';
import { newRunId, pollRun } from '../lib/run.js';
(function () {
  const { useState, useRef, useEffect } = React;
  function I(props) { return React.createElement(window.MlIcon, props); }
  // prototype relationship key ↔ backend relationships_to_me value
  const REL_OUT = { alt: "alternative", look: "lookalike", comp: "complementary" };
  const REL_IN = { alternative: "alt", lookalike: "look", complementary: "comp" };
  const pct = (c) => (c == null ? 0 : Math.round(c <= 1 ? c * 100 : c));
  const siteIdOf = (site) => (site && (site.site_id != null ? site.site_id : site.id));

  const REL = [["alt", "Alternative", "alt", "Solves the same problem a different way"], ["look", "Lookalike", "look", "A close, direct equivalent"], ["comp", "Complementary", "comp", "Pairs with what you offer"]];

  function Modal({ title, sub, wide, sm, onClose, children, foot }) {
    return (
      <div className="bi-scrim" onMouseDown={ev => { if (ev.target === ev.currentTarget) onClose(); }}>
        <div className={"bi-modal" + (wide ? " bi-modal--wide" : "") + (sm ? " bi-modal--sm" : "")} role="dialog" aria-modal="true">
          <div className="bi-modal__head">
            <div><div className="bi-modal__title">{title}</div>{sub && <div className="bi-modal__sub">{sub}</div>}</div>
            <button className="bi-iconbtn bi-modal__close" onClick={onClose} aria-label="Close"><I name="x" size={18} /></button>
          </div>
          {children}
          {foot && <div className="bi-modal__foot">{foot}</div>}
        </div>
      </div>
    );
  }

  // Progress for a call in flight. `run` is the server's own milestone state
  // (GET /api/runs/:id); `labels` is the fallback shown before the first poll
  // lands, or when the server reports nothing. The steps used to advance on a
  // 700ms timer whatever the server was doing, so a stalled call looked busy
  // and a failed one looked nearly finished.
  function FetchProgress({ run, labels }) {
    if (!run) {
      return (
        <div className="bi-fetch-progress">
          {labels.map((l, i) => (
            <div className={"bi-fetch-step" + (i === 0 ? " bi-fetch-step--active" : "")} key={i}>
              <span className="bi-fetch-step__dot">{i === 0 ? <span className="ml-spinner ml-spinner--sm ml-spinner--ai" style={{ width: 14, height: 14 }} /> : i + 1}</span>
              <span className="bi-fetch-step__label">{l}</span>
            </div>
          ))}
        </div>
      );
    }
    const current = Math.max(1, run.step);
    return (
      <div className="bi-fetch-progress">
        <div className="ml-progress-row" style={{ marginBottom: 12 }}>
          <div className="ml-progress"><div className="ml-progress__bar ml-progress__bar--ai" style={{ width: run.pct + "%" }} /></div>
          <span className="ml-progress-row__pct">{run.pct}%</span>
        </div>
        <div className="bi-fetch-step bi-fetch-step--active">
          <span className="bi-fetch-step__dot">
            {run.status === "failed" ? <I name="alert-triangle" size={14} /> : <span className="ml-spinner ml-spinner--sm ml-spinner--ai" style={{ width: 14, height: 14 }} />}
          </span>
          <span className="bi-fetch-step__label">
            Step {current} of {run.steps} · {run.label}
            {run.note ? <span style={{ color: "var(--text-muted)" }}> · {run.note}</span> : null}
          </span>
        </div>
      </div>
    );
  }

  function RelPills({ active, onToggle }) {
    return <div className="bi-proposed-card__pills" onClick={e => e.stopPropagation()}>{REL.map(([id, label]) => <button key={id} className={"bi-rel-pill bi-rel-pill--" + id + (active.includes(id) ? " on" : "")} onClick={() => onToggle(id)}>{label}</button>)}</div>;
  }

  function ProposedCard({ p, sel, onToggle, onRel }) {
    return (
      <div className={"bi-proposed-card" + (sel ? " bi-proposed-card--on" : "")} onClick={onToggle}>
        <label className="hds-check" onClick={e => e.stopPropagation()}><input type="checkbox" checked={sel} onChange={onToggle} /><span className="hds-check__box"><I name="check" size={12} /></span></label>
        <div className="bi-proposed-card__main">
          <div className="bi-proposed-card__name">{p.name}{p.cat && <span className="bi-cat-tag">{p.cat}</span>}</div>
          {p.url && <div className="bi-proposed-card__url">{p.url}</div>}
          <div className="bi-proposed-card__sum">{p.sum}</div>
          <RelPills active={p.rels} onToggle={(r) => onRel(p.id, r)} />
        </div>
        <div className="bi-conf">{p.conf}%</div>
      </div>
    );
  }

  // ---- Add / Edit modal (1I) ----
  function AddEditModal({ cfg, edit, onClose, site, onSaved }) {
    const DS = window.HuddleDesignSystem_775113;
    const { Input, Select } = DS;
    const siteId = site && (site.site_id != null ? site.site_id : site.id);
    const [tab, setTab] = useState("manual");
    const [name, setName] = useState(edit ? (edit.name || "") : "");
    const [url, setUrl] = useState(edit ? (edit.url || "") : "");
    const [rels, setRels] = useState(edit && edit.rels && edit.rels.length ? edit.rels : ["alt"]);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);
    const saveManual = async () => {
      if (!name.trim() || siteId == null) { if (siteId == null) setErr("No active site."); return; }
      setSaving(true); setErr(null);
      const payload = {
        site_id: siteId, entity_type: cfg.noun, name: name.trim(),
        url: cfg.hasUrl ? (url.trim() || null) : null,
        data: { relationships_to_me: rels.map(r => REL_OUT[r]).filter(Boolean) },
        source: "manual", validation_status: "validated",
      };
      try {
        if (edit) await api(`/bi/entities/${edit.id}`, { method: "PATCH", body: JSON.stringify(payload) });
        else await api(`/bi/entities`, { method: "POST", body: JSON.stringify(payload) });
        onSaved && onSaved();
        onClose();
      } catch (e) { setErr(e.message || "Save failed"); setSaving(false); }
    };
    // ── Fetch from URL (wired to POST /bi/extract-from-url) ────
    // Was a setInterval over three invented competitors with a save button that
    // discarded them. It now reads the page, shows what the model actually
    // extracted (editable before saving), and saves the entity plus whichever
    // proposed neighbours are ticked.
    const [phase, setPhase] = useState("idle"); // idle | running | results
    const [run, setRun] = useState(null);
    const [fetchUrl, setFetchUrl] = useState("");
    const [extra, setExtra] = useState({});   // extracted profile fields beyond name/url
    const [props, setProps] = useState([]);
    const [sel, setSel] = useState([]);
    const t = useRef(null);
    useEffect(() => () => { if (typeof t.current === "function") t.current(); }, []);
    const labels = ["Fetching the page", "Extracting profile with AI", "Proposing related entities"];

    const runFetch = async () => {
      const src = fetchUrl.trim();
      if (!src) { setErr("Enter a URL to fetch."); return; }
      if (siteId == null) { setErr("No active site."); return; }
      setPhase("running"); setRun(null); setErr(null);
      const runId = newRunId();
      t.current = pollRun(runId, setRun);
      try {
        const res = await api(`/bi/extract-from-url`, { method: "POST", body: JSON.stringify({
          run_id: runId, site_id: siteId, url: src, entity_type: cfg.noun,
          // Without a field list the endpoint uses competitor-worded prompts for
          // every module, so Partners and Products would be read as rivals.
          ...(cfg.fields ? { fields: cfg.fields.map(f => ({ key: f.key, label: f.label })) } : {}),
        }) });
        t.current();
        const ex = (res && res.extracted) || {};
        setName(ex.name || "");
        if (cfg.hasUrl) setUrl(ex.url || src);
        // Everything the model read beyond name/url: kept so the profile it
        // built is what gets saved, not just the two fields on this form.
        setExtra(ex.data && typeof ex.data === "object" ? ex.data : {});
        const exRels = (ex.relationships_to_me || []).map(r => REL_IN[r]).filter(Boolean);
        if (exRels.length) setRels(exRels);
        const list = ((res && res.proposed_neighbours) || []).map((p, i) => ({
          id: i, name: p.name || "", url: p.url || "", sum: p.summary || "", conf: pct(p.confidence),
          rels: (p.relationships_to_me || []).map(r => REL_IN[r]).filter(Boolean), cat: p.category || "",
        })).filter(p => p.name);
        setProps(list);
        setSel(list.map(p => p.id));   // additive by default; untick to skip
        setPhase("results");
      } catch (e) { t.current(); setErr(e.message || "Fetch failed"); setPhase("idle"); }
    };

    // Saves the extracted entity as validated (the user has just reviewed it),
    // and each ticked neighbour as a pending suggestion for later review.
    const saveFetched = async () => {
      if (!name.trim() || siteId == null) { setErr(name.trim() ? "No active site." : "Name is required."); return; }
      setSaving(true); setErr(null);
      try {
        await api(`/bi/entities`, { method: "POST", body: JSON.stringify({
          site_id: siteId, entity_type: cfg.noun, name: name.trim(),
          url: cfg.hasUrl ? (url.trim() || null) : null,
          data: { ...extra, relationships_to_me: rels.map(r => REL_OUT[r]).filter(Boolean), from_source: "url", from: fetchUrl.trim() },
          source: "fetched", validation_status: "validated",
        }) });
        for (const p of props.filter(p => sel.includes(p.id))) {
          await api(`/bi/entities`, { method: "POST", body: JSON.stringify({
            site_id: siteId, entity_type: cfg.noun, name: p.name, url: cfg.hasUrl ? (p.url || null) : null,
            data: { relationships_to_me: p.rels.map(r => REL_OUT[r]).filter(Boolean), from_source: "url", from: fetchUrl.trim() },
            source: "ai_suggested", validation_status: "pending",
          }) });
        }
        onSaved && onSaved();
        onClose();
      } catch (e) { setErr(e.message || "Save failed"); setSaving(false); }
    };

    const toggleRel = (r) => setRels(s => s.includes(r) ? s.filter(x => x !== r) : [...s, r]);
    const toggleSel = (id) => setSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
    const cardRel = (id, r) => setProps(ps => ps.map(p => p.id === id ? { ...p, rels: p.rels.includes(r) ? p.rels.filter(x => x !== r) : [...p.rels, r] } : p));

    const foot = phase === "results"
      ? <><button className="bi-btn" onClick={onClose} disabled={saving}>Cancel</button><button className="bi-btn bi-btn--primary" disabled={saving || !name.trim()} onClick={saveFetched}><I name="check" size={16} />{saving ? "Saving…" : `Validate & save (${sel.length + 1})`}</button></>
      : tab === "manual"
        ? <><button className="bi-btn" onClick={onClose}>Cancel</button><button className="bi-btn bi-btn--primary" disabled={saving || !name.trim()} onClick={saveManual}>{saving ? "Saving…" : "Save"}</button></>
        : <><button className="bi-btn" onClick={onClose}>Cancel</button><button className="bi-btn bi-btn--ai" disabled={phase === "running" || !fetchUrl.trim()} onClick={runFetch}><I name="sparkles" size={16} />Fetch &amp; analyze</button></>;

    return (
      <Modal title={edit ? "Edit " + cfg.noun : "Add a " + cfg.noun} onClose={onClose} foot={foot}>
        {!edit && phase !== "results" && (
          <div className="bi-modal__tabs"><div className="hds-tabs">
            <button className={"hds-tab " + (tab === "manual" ? "hds-tab--active" : "")} onClick={() => setTab("manual")}>Manual entry</button>
            <button className={"hds-tab " + (tab === "fetch" ? "hds-tab--active" : "")} onClick={() => setTab("fetch")}><I name="sparkles" size={15} />Fetch from URL</button>
          </div></div>
        )}
        <div className="bi-modal__body">
          {tab === "manual" && phase !== "results" && (
            <div className="bi-form-grid">
              <div><label className="bi-field__label">Name <span className="bi-field__req">*</span></label><Input value={name} onChange={e => setName(e.target.value)} placeholder={"e.g. a " + cfg.noun + " you track"} /></div>
              {cfg.hasUrl && <div><label className="bi-field__label">Website</label><Input leadingIcon="globe" value={url} onChange={e => setUrl(e.target.value)} placeholder="example.com" /></div>}
              {!cfg.hideRelationships && <div>
                <label className="bi-field__label">Relationship</label>
                <div className="bi-rel-multi">{REL.map(([id, label, cls, hint]) => <button key={id} className={"bi-rel-card bi-rel-card--" + cls + (rels.includes(id) ? " on" : "")} onClick={() => toggleRel(id)}><div className="bi-rel-card__name">{label}</div><div className="bi-rel-card__hint">{hint}</div></button>)}</div>
              </div>}
              {err && <div className="bi-hint" style={{ color: "var(--danger)" }}>{err}</div>}
              <div className="bi-hint">Manual entries are saved as <b>validated</b> immediately.</div>
            </div>
          )}
          {tab === "fetch" && phase === "idle" && (
            <div className="bi-form-grid">
              <div><label className="bi-field__label">URL to fetch <span className="bi-field__req">*</span></label><Input leadingIcon="link" value={fetchUrl} onChange={e => setFetchUrl(e.target.value)} placeholder="example.com" /></div>
              {err && <div className="bi-hint" style={{ color: "var(--danger)" }}>{err}</div>}
              <div className="bi-hint">We'll read the page, extract a profile, and propose related entities you can review. Relationship tokens: <b>Alternative</b> · <b>Lookalike</b> · <b>Complementary</b>.</div>
            </div>
          )}
          {phase === "running" && <FetchProgress run={run} labels={labels} />}
          {phase === "results" && (
            <div style={{ paddingTop: 6 }}>
              <div className="bi-results-head"><I name="check-circle" size={16} style={{ color: "var(--success)" }} />Extracted <b style={{ fontWeight: 700 }}>{name || "this page"}</b> · edit before saving</div>
              <div className="bi-form-grid" style={{ paddingTop: 0 }}>
                <div><label className="bi-field__label">Name <span className="bi-field__req">*</span></label><Input value={name} onChange={e => setName(e.target.value)} /></div>
                {cfg.hasUrl && <div><label className="bi-field__label">Website</label><Input value={url} onChange={e => setUrl(e.target.value)} leadingIcon="globe" /></div>}
                {!cfg.hideRelationships && <div><label className="bi-field__label">Relationship</label><div className="bi-rel-multi">{REL.map(([id, label, cls, hint]) => <button key={id} className={"bi-rel-card bi-rel-card--" + cls + (rels.includes(id) ? " on" : "")} onClick={() => toggleRel(id)}><div className="bi-rel-card__name">{label}</div><div className="bi-rel-card__hint">{hint}</div></button>)}</div></div>}
              </div>
              {err && <div className="bi-hint" style={{ color: "var(--danger)" }}>{err}</div>}
              {props.length > 0 ? (
                <>
                  <div className="bi-results-head" style={{ marginTop: 18 }}>Proposed related entities<span className="bi-conf" style={{ color: "var(--text-muted)" }}>{sel.length} selected</span></div>
                  <div className="bi-proposed-list">{props.map(p => <ProposedCard key={p.id} p={p} sel={sel.includes(p.id)} onToggle={() => toggleSel(p.id)} onRel={cardRel} />)}</div>
                </>
              ) : (
                <div className="bi-hint" style={{ marginTop: 14 }}>No related entities were proposed from this page. The extracted profile above is still yours to save.</div>
              )}
            </div>
          )}
        </div>
      </Modal>
    );
  }

  // ---- Grow-from cross-fetch (wired to POST /bi/cross-fetch) ----
  //
  // Two things this screen owes the user, both of which it used to withhold.
  //
  // WHICH ROWS. The request carried a source module and nothing else, so a grow
  // from products was a grow from every product, in whatever order they came
  // back. The API had accepted `source_ids` all along and no screen ever sent
  // it. The picker below is that field, reachable.
  //
  // HOW MANY OF THEM. The prompt carries at most `cap` source rows (the server
  // reports the number, this file never hardcodes it) and drops the rest. A user
  // with 137 products saw "12 new products found" and had no way to know the run
  // had read 40 of them. Every count here is stated against its total, and rows
  // that fall past the cap are marked in the list BEFORE the request is spent,
  // so the choice of which 40 is the user's rather than the id order's.
  const GROW_SRC = {
    business_profile: ["Business Profile", "briefcase"], competitor: ["Competitors", "flag"],
    industry: ["Industry", "globe"], partner: ["Partners", "git-branch"], product: ["Products", "box"],
    keywords: ["Keywords", "search"], web: ["Live web", "globe"],
  };
  function GrowModal({ cfg, onClose, site, onSaved }) {
    const siteId = siteIdOf(site);
    const [web, setWeb] = useState(true);
    const [source, setSource] = useState(null);
    const [phase, setPhase] = useState("pick"); // pick | running | results
    const [run, setRun] = useState(null);
    const [proposals, setProposals] = useState([]);
    const [sel, setSel] = useState([]);
    const [webInfo, setWebInfo] = useState(null);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);
    // The source module's own rows: what this run would read, before it runs.
    const [src, setSrc] = useState({ loading: false, error: null, data: null });
    const [picked, setPicked] = useState([]);
    const t = useRef(null);
    useEffect(() => () => { if (typeof t.current === "function") t.current(); }, []);
    const sources = ["business_profile", "competitor", "industry", "partner", "product", "keywords", "web"]
      .filter(s => s !== cfg.noun).map(s => [s, GROW_SRC[s][0], GROW_SRC[s][1]]);
    const labels = ["Reading source entities", "Deriving candidates with AI", "Classifying & de-duplicating"];
    const toggleSel = (id) => setSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
    // "3 competitors entries" is clumsy and "3 keywords entries" is wrong;
    // keywords are their own noun, modules get "<label> entries".
    const srcEntries = (s, n) => s === "keywords" ? (n === 1 ? "keyword" : "keywords") : `${GROW_SRC[s][0].toLowerCase()} ${n === 1 ? "entry" : "entries"}`;

    // The chosen source's entities, from the same read the run itself uses, so
    // the list cannot describe a run that does something else. The live web has
    // no rows to pick from and is skipped.
    useEffect(() => {
      if (!source || source === "web" || siteId == null) { setSrc({ loading: false, error: null, data: null }); setPicked([]); return; }
      let cancel = false;
      setSrc({ loading: true, error: null, data: null });
      (async () => {
        try {
          const d = await api(`/bi/cross-fetch/sources?site_id=${siteId}&source=${encodeURIComponent(source)}`);
          if (cancel) return;
          setSrc({ loading: false, error: null, data: d });
          // Everything ticked, which is what the run did before there was a
          // picker. The point of the list is to let the user narrow it, not to
          // make them opt in to their own data.
          setPicked((d.entities || []).map(e => e.id));
        } catch (e) {
          // A list that could not be read is not a source with nothing in it.
          // The run is still allowed: with no ids sent it behaves exactly as it
          // did before, and saying so is better than blocking on a side read.
          if (!cancel) setSrc({ loading: false, error: e.message || "Could not read what this source holds.", data: null });
        }
      })();
      return () => { cancel = true; };
    }, [source, siteId]);

    const entities = (src.data && src.data.entities) || [];
    const cap = (src.data && src.data.cap) || 0;
    // The selection in the order the server reads it (the list is already in
    // that order), so the ones past the cap are the ones the run will drop.
    const pickedInOrder = entities.filter(e => picked.includes(e.id));
    const usedIds = cap ? new Set(pickedInOrder.slice(0, cap).map(e => e.id)) : new Set(pickedInOrder.map(e => e.id));
    const overCap = Math.max(0, pickedInOrder.length - (cap || pickedInOrder.length));
    const togglePick = (id) => setPicked(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

    const start = async () => {
      if (!source || siteId == null) return;
      if (source !== "web" && entities.length && !picked.length) { setErr("Pick at least one entity to grow from."); return; }
      setPhase("running"); setRun(null); setErr(null);
      // A grounded grow runs several web searches inside one request; the
      // server reports each hop, so the panel can say "3 web searches so far"
      // instead of ticking three fixed labels on a timer.
      const runId = newRunId();
      t.current = pollRun(runId, setRun);
      try {
        // Ids only when the user narrowed it. Sending every id would mean the
        // same thing, but an omitted field is the honest way to say "all of
        // them" and keeps the server's own refusals worded for the right case.
        const narrowed = entities.length > 0 && picked.length !== entities.length;
        const res = await api(`/bi/cross-fetch`, { method: "POST", body: JSON.stringify({
          run_id: runId, site_id: siteId, target_type: cfg.noun, source, web_search: web,
          ...(narrowed ? { source_ids: picked } : {}),
        }) });
        t.current();
        const list = ((res && res.proposals) || []).map((p, i) => ({
          id: i, name: p.name || "", url: p.url || "", sum: p.summary || "", conf: pct(p.confidence),
          rels: (p.relationships_to_me || []).map(r => REL_IN[r]).filter(Boolean), cat: p.category || "", from: p.from || source,
        }));
        setProposals(list); setSel(list.map(p => p.id));
        setWebInfo({
          grounded: !!(res && res.web_grounded), n: (res && res.web_searches) || 0,
          // A short list should be explainable. Entities already tracked are
          // filtered out server-side; saying how many keeps "only 2 found" from
          // looking like the source was thin.
          skipped: (res && res.skipped_tracked) || [],
          // What the run was derived from. Carried back from the server rather
          // than recomputed here, so the screen reports the run that happened.
          cov: (res && res.source_coverage) || null,
        });
        setPhase("results");
      } catch (e) { t.current(); setErr(e.message || "Grow failed"); setPhase("pick"); }
    };

    const save = async () => {
      const chosen = proposals.filter(p => sel.includes(p.id));
      if (!chosen.length || siteId == null) return;
      setSaving(true); setErr(null);
      try {
        for (const p of chosen) {
          await api(`/bi/entities`, { method: "POST", body: JSON.stringify({
            site_id: siteId, entity_type: cfg.noun, name: p.name, url: cfg.hasUrl ? (p.url || null) : null,
            data: { relationships_to_me: p.rels.map(r => REL_OUT[r]).filter(Boolean), from_source: source, from: p.from },
            source: "ai_suggested", validation_status: "pending",
          }) });
        }
        onSaved && onSaved(); onClose();
      } catch (e) { setErr(e.message || "Save failed"); setSaving(false); }
    };

    const foot = phase === "results"
      ? <><button className="bi-btn" onClick={onClose}>Cancel</button><button className="bi-btn bi-btn--primary" disabled={saving || !sel.length} onClick={save}>{saving ? "Adding…" : `Add selected (${sel.length})`}</button></>
      : <><button className="bi-btn" onClick={onClose}>Cancel</button><button className="bi-btn bi-btn--ai" disabled={!source || phase === "running" || src.loading || (entities.length > 0 && !picked.length)} onClick={start}><I name="download" size={16} />{usedIds.size && entities.length ? `Grow from ${usedIds.size} selected` : "Grow from source"}</button></>;

    return (
      <Modal title={"Grow " + cfg.title.toLowerCase() + " from…"} sub="Discover new entities from a source and review each before it's tracked." onClose={onClose} foot={foot}>
        <div className="bi-modal__body">
          {err && <div className="bi-hint" style={{ color: "var(--danger)" }}>{err}</div>}
          {phase === "pick" && (
            <div style={{ paddingTop: 12 }}>
              <div className="bi-web-toggle">
                <label className="hds-switch"><input type="checkbox" checked={web} onChange={e => setWeb(e.target.checked)} /><span className="hds-switch__track"><span className="hds-switch__thumb" /></span></label>
                <div className="bi-web-toggle__meta"><div className="bi-web-toggle__t">Search the live web</div><div className="bi-web-toggle__h">More accurate &amp; current, uses more tokens.</div></div>
                <I name="globe" size={16} style={{ color: web ? "var(--info)" : "var(--text-disabled)" }} />
              </div>
              <div className="bi-cf-sources">
                {sources.map(([id, name, icon]) => <button key={id} className={"bi-cf-source" + (source === id ? " bi-cf-source--on" : "")} onClick={() => setSource(id)}><span className="bi-cf-source__icon"><I name={icon} size={16} /></span><span><div className="bi-cf-source__name">{name}</div></span></button>)}
              </div>
              {source && source !== "web" && (
                <div className="bi-cf-pick">
                  {src.loading && <div className="bi-subempty" style={{ padding: 20 }}>Reading what this source holds…</div>}
                  {src.error && <div className="bi-hint">{src.error} The run will use every {srcEntries(source, 1)}, as it did before.</div>}
                  {src.data && !entities.length && (
                    <div className="bi-hint">{src.data.empty_reason || `There is nothing in ${GROW_SRC[source][0]} to grow from yet.`}</div>
                  )}
                  {src.data && entities.length > 0 && (
                    <>
                      <div className="bi-cf-pick__head">
                        <span className="bi-cf-pick__title">Grow from which of your {src.data.total} {srcEntries(source, src.data.total)}</span>
                        <span className="bi-cf-pick__count">{usedIds.size} of {src.data.total} will be read</span>
                        <button className="bi-btn bi-btn--sm" onClick={() => setPicked(entities.map(e => e.id))}>All</button>
                        <button className="bi-btn bi-btn--sm" onClick={() => setPicked([])}>None</button>
                      </div>
                      {/* The cap, said before the request is spent rather than
                          discovered afterwards. Written here rather than taken
                          from the server's `says`, because the number that
                          matters is what THIS selection costs, and the server
                          has not seen the selection yet. */}
                      {overCap > 0 && (
                        <div className="bi-cf-cap"><I name="alert-triangle" size={13} />You ticked {pickedInOrder.length}, and one run carries {cap}. The {overCap} greyed below will not be read. Untick some to choose which {cap}.</div>
                      )}
                      <div className="bi-cf-pick__list">
                        {entities.map(e => {
                          const on = picked.includes(e.id);
                          const out = on && !usedIds.has(e.id);
                          return (
                            <label key={e.id} className={"bi-cf-pick__row" + (out ? " bi-cf-pick__row--out" : "")}>
                              <span className="hds-check"><input type="checkbox" checked={on} onChange={() => togglePick(e.id)} /><span className="hds-check__box"><I name="check" size={12} /></span></span>
                              <span className="bi-cf-pick__main">
                                <span className="bi-cf-pick__name">{e.name}</span>
                                {e.url && <span className="bi-cf-pick__url">{e.url}</span>}
                              </span>
                              {/* Said out loud because it is asymmetric: a
                                  rejected row still seeds a grow, while a
                                  rejected row on the target side is filtered
                                  out of the already-tracked list. */}
                              {e.owner_flag && <span className="bi-cf-pick__flag">your business</span>}
                              {e.validation_status && e.validation_status !== "validated" && <span className="bi-cf-pick__flag">{e.validation_status}</span>}
                              {out && <span className="bi-cf-pick__flag bi-cf-pick__flag--out">past the cap</span>}
                            </label>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          {phase === "running" && <FetchProgress run={run} labels={labels} />}
          {phase === "results" && (
            <div style={{ paddingTop: 8 }}>
              <div className="bi-results-head">{webInfo && webInfo.grounded ? <span className="bi-web-badge bi-web-badge--live"><I name="globe" size={12} />{webInfo.n} live web searches</span> : <span className="bi-web-badge bi-web-badge--quick">quick · no web search</span>}<span style={{ marginLeft: "auto", color: "var(--text-muted)", fontWeight: 400 }}>{proposals.length} new{webInfo && webInfo.skipped && webInfo.skipped.length ? ` · ${webInfo.skipped.length} already tracked` : ""}</span></div>
              {/* What these proposals were derived from. A count of finds with
                  no denominator reads as a sweep of the source module, and on a
                  module larger than the cap it never was one. */}
              {webInfo && webInfo.cov && webInfo.cov.total > 0 && (
                <div className={"bi-cf-cap" + (webInfo.cov.truncated ? "" : " bi-cf-cap--clear")}>
                  {webInfo.cov.truncated ? <I name="alert-triangle" size={13} /> : <I name="check" size={13} />}
                  {webInfo.cov.truncated
                    ? `Derived from ${webInfo.cov.used} of your ${webInfo.cov.total} ${srcEntries(source, webInfo.cov.total)}${webInfo.cov.picked ? " that you picked" : ""}. ${webInfo.cov.says}`
                    : `Derived from ${webInfo.cov.total} ${srcEntries(source, webInfo.cov.total)}${webInfo.cov.picked ? " that you picked" : ", all of them"}.`}
                </div>
              )}
              {proposals.length === 0
                ? <div className="bi-hint">{webInfo && webInfo.skipped && webInfo.skipped.length
                    ? `Everything this source proposed is already tracked (${webInfo.skipped.slice(0, 3).join(", ")}${webInfo.skipped.length > 3 ? ", …" : ""}). Try the live web, or a different source.`
                    : "No new entities found from this source. Try the live web, or a different source."}</div>
                : <div className="bi-proposed-list">{proposals.map(p => <ProposedCard key={p.id} p={p} sel={sel.includes(p.id)} onToggle={() => toggleSel(p.id)} onRel={() => {}} />)}</div>}
            </div>
          )}
        </div>
      </Modal>
    );
  }

  // ---- shared dialogs (1K) ----
  function ConfirmDialog({ danger, title, body, confirmLabel, onClose, onConfirm }) {
    const done = () => { if (onConfirm) onConfirm(); else onClose(); };
    return (
      <Modal sm onClose={onClose} title="" foot={<><button className="bi-btn" onClick={onClose}>Cancel</button><button className={"bi-btn " + (danger ? "bi-btn--danger" : "bi-btn--primary")} onClick={done} style={danger ? { background: "var(--danger)", borderColor: "var(--danger)", color: "#fff" } : null}>{confirmLabel}</button></>}>
        <div className="bi-modal__body" style={{ display: "flex", gap: 14, paddingTop: 4, paddingBottom: 8 }}>
          <span className={"bi-confirm-icon bi-confirm-icon--" + (danger ? "danger" : "primary")}><I name={danger ? "trash" : "sparkles"} size={22} /></span>
          <div><div className="bi-modal__title" style={{ fontSize: "var(--text-md)" }}>{title}</div><div className="bi-modal__sub">{body}</div></div>
        </div>
      </Modal>
    );
  }
  function AlertDialog({ onClose }) {
    return (
      <Modal sm onClose={onClose} title="" foot={<button className="bi-btn bi-btn--primary" onClick={onClose}>OK</button>}>
        <div className="bi-modal__body" style={{ display: "flex", gap: 14, paddingTop: 4, paddingBottom: 8 }}>
          <span className="bi-confirm-icon bi-confirm-icon--primary"><I name="info" size={22} /></span>
          <div><div className="bi-modal__title" style={{ fontSize: "var(--text-md)" }}>Set a market first</div><div className="bi-modal__sub">BI suggestions are constrained to a single market. Set one in your Business Profile to grow entities.</div></div>
        </div>
      </Modal>
    );
  }
  function ShareModal({ onClose, site, sites, entity, onSaved }) {
    const siteId = siteIdOf(site);
    const dests = (sites || []).filter(s => s.id !== (site && site.id));
    const [sel, setSel] = useState([]);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);
    const all = dests.length > 0 && sel.length === dests.length;
    const toggle = (id) => setSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
    const send = async () => {
      if (!entity || !sel.length || siteId == null) return;
      setSaving(true); setErr(null);
      try {
        await api(`/bi/entities/${entity.id}/share`, { method: "POST", body: JSON.stringify({ site_id: siteId, dest_site_ids: sel, from_label: (site && site.name) || "" }) });
        onSaved && onSaved(); onClose();
      } catch (e) { setErr(e.message || "Share failed"); setSaving(false); }
    };
    return (
      <Modal title="Share to a site" sub="The entity lands in that site's Inbox for review before it's tracked there." onClose={onClose} foot={<><button className="bi-btn" onClick={onClose}>Cancel</button><button className="bi-btn bi-btn--primary" disabled={saving || !sel.length} onClick={send}><I name="share" size={16} />{saving ? "Sending…" : `Send (${sel.length})`}</button></>}>
        <div className="bi-modal__body" style={{ paddingTop: 8 }}>
          {err && <div className="bi-hint" style={{ color: "var(--danger)" }}>{err}</div>}
          {dests.length === 0
            ? <div className="bi-hint">No other sites to share to.</div>
            : <>
              <label className="bi-checkrow" style={{ borderBottom: "1px solid var(--border-subtle)", borderRadius: 0 }}><span className="hds-check"><input type="checkbox" checked={all} onChange={() => setSel(all ? [] : dests.map(s => s.id))} /><span className="hds-check__box"><I name="check" size={12} /></span></span><span className="bi-checkrow__name" style={{ fontWeight: 600 }}>Select all sites</span></label>
              <div className="bi-checklist">{dests.map(s => <label className="bi-checkrow" key={s.id}><span className="hds-check"><input type="checkbox" checked={sel.includes(s.id)} onChange={() => toggle(s.id)} /><span className="hds-check__box"><I name="check" size={12} /></span></span><span className="bi-checkrow__name">{s.name}</span><span className="bi-checkrow__sub">{s.url}</span></label>)}</div>
            </>}
        </div>
      </Modal>
    );
  }
  function VariantModal({ onClose }) {
    const DS = window.HuddleDesignSystem_775113;
    const { Input, Select } = DS;
    const [type, setType] = useState("geographic");
    return (
      <Modal title="Add a sub-product" sub="Create a variant that rolls up to its parent." onClose={onClose} foot={<><button className="bi-btn" onClick={onClose}>Cancel</button><button className="bi-btn bi-btn--primary" onClick={onClose}>Add variant</button></>}>
        <div className="bi-modal__body"><div className="bi-form-grid">
          <div><label className="bi-field__label">Variant type</label><Select value={type} onChange={e => setType(e.target.value)}><option value="geographic">Geographic</option><option value="spec">Specialization</option><option value="custom">Custom</option></Select></div>
          {type === "geographic"
            ? <div><label className="bi-field__label">Country</label><Select defaultValue="fr"><option value="fr">France</option><option value="de">Germany</option><option value="es">Spain</option></Select></div>
            : <div><label className="bi-field__label">Label</label><Input placeholder={type === "spec" ? "e.g. Enterprise tier" : "Custom label"} /></div>}
          <div><label className="bi-field__label">Name <span className="bi-field__req">*</span></label><Input placeholder="Variant name" /></div>
        </div></div>
      </Modal>
    );
  }

  window.MlBiModals = { _Modal: Modal, AddEditModal, GrowModal, ConfirmDialog, AlertDialog, ShareModal, VariantModal, ProviderModal, AddOfferingsModal };

  // ---- Add a provider to one product ----
  // Wired to POST /bi/ingest-offerings, the same merge the offerings reader
  // uses: one offering (this product's own name + service line) carrying the
  // new provider, so it attaches to the existing row instead of creating a
  // second one. Previously the form's "Add provider" button called onClose()
  // and the typed provider was discarded.
  function ProviderModal({ entity, site, onSaved, onClose }) {
    const DS = window.HuddleDesignSystem_775113; const { Input, Select } = DS;
    const siteId = siteIdOf(site);
    const [kind, setKind] = useState("comp");     // us | comp | partner | market
    const [name, setName] = useState("");
    const [url, setUrl] = useState("");
    const [pricing, setPricing] = useState("");
    const [linkId, setLinkId] = useState("");     // tracked entity this provider IS
    const [tracked, setTracked] = useState({ competitor: [], partner: [] });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);

    // Linking to a tracked competitor/partner is what earns the "tracked" badge
    // on the provider row, so the picker needs the real lists.
    useEffect(() => {
      if (siteId == null) return;
      let cancel = false;
      Promise.all([
        api(`/bi/entities?site_id=${siteId}&entity_type=competitor&owner_flag=0`).catch(() => []),
        api(`/bi/entities?site_id=${siteId}&entity_type=partner&owner_flag=0`).catch(() => []),
      ]).then(([c, p]) => {
        if (cancel) return;
        setTracked({ competitor: Array.isArray(c) ? c : [], partner: Array.isArray(p) ? p : [] });
      });
      return () => { cancel = true; };
    }, [siteId]);

    const linkType = kind === "comp" ? "competitor" : kind === "partner" ? "partner" : null;
    const onLink = (id) => {
      setLinkId(id);
      const hit = linkType && tracked[linkType].find(e => String(e.id) === String(id));
      if (hit) { setName(hit.name || ""); setUrl(hit.url || ""); }
    };

    const save = async () => {
      if (siteId == null) { setErr("No active site."); return; }
      if (!entity || !entity.name) { setErr("No product in context."); return; }
      const nm = kind === "us" ? (name.trim() || "Us") : name.trim();
      if (!nm) { setErr("Name is required."); return; }
      setSaving(true); setErr(null);
      try {
        const res = await api(`/bi/ingest-offerings`, { method: "POST", body: JSON.stringify({
          site_id: siteId,
          provider: {
            kind: kind === "comp" ? "competitor" : kind,
            name: nm, url: url.trim(),
            ...(linkType && linkId ? { ref_entity_id: Number(linkId), ref_entity_type: linkType } : {}),
          },
          source_url: url.trim(),
          // The product's own name and service line are the merge key, so this
          // lands on the row the user is looking at rather than making a new one.
          offerings: [{ name: entity.name, category: entity.serviceLineId || "", pricing: pricing.trim() }],
        }) });
        onSaved && onSaved(res);
        onClose();
      } catch (e) { setErr(e.message || "Could not add the provider."); setSaving(false); }
    };

    return (
      <Modal title="Add a provider" sub={entity ? `Who else offers "${entity.name}" - with their pricing and source.` : "Who else offers this - with their pricing and source."}
             onClose={saving ? () => {} : onClose}
             foot={<><button className="bi-btn" onClick={onClose} disabled={saving}>Cancel</button><button className="bi-btn bi-btn--primary" disabled={saving || (kind !== "us" && !name.trim())} onClick={save}>{saving ? "Adding…" : "Add provider"}</button></>}>
        <div className="bi-modal__body"><div className="bi-form-grid">
          <div><label className="bi-field__label">Who offers it</label><div className="bi-rel-multi" style={{ gridTemplateColumns: "repeat(4,1fr)" }}>{[["us", "★ Us"], ["comp", "Competitor"], ["partner", "Partner"], ["market", "Market"]].map(([id, l]) => <button key={id} className={"bi-rel-card" + (kind === id ? " on" : "")} style={kind === id ? { borderColor: "var(--primary)", background: "var(--primary-tint)" } : null} onClick={() => { setKind(id); setLinkId(""); }}><div className="bi-rel-card__name" style={{ fontSize: 12 }}>{l}</div></button>)}</div></div>
          {linkType && tracked[linkType].length > 0 && (
            <div>
              <label className="bi-field__label">Link to a tracked {linkType}</label>
              <Select value={linkId} onChange={e => onLink(e.target.value)}>
                <option value="">Not tracked (type the name below)</option>
                {tracked[linkType].map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </Select>
            </div>
          )}
          <div><label className="bi-field__label">Name {kind !== "us" && <span className="bi-field__req">*</span>}</label><Input value={name} onChange={e => setName(e.target.value)} placeholder={kind === "us" ? "Us" : "e.g. Stripe Atlas"} /></div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div><label className="bi-field__label">Website</label><Input leadingIcon="globe" value={url} onChange={e => setUrl(e.target.value)} placeholder="example.com" /></div>
            <div><label className="bi-field__label">Pricing</label><Input value={pricing} onChange={e => setPricing(e.target.value)} placeholder="€199 / mo" /></div>
          </div>
          {err && <div className="bi-hint" style={{ color: "var(--danger)" }}>{err}</div>}
        </div></div>
      </Modal>
    );
  }

  // ---- Add offerings from a source ----
  // Wired to POST /bi/extract-offerings (read a page) + /bi/ingest-offerings
  // (merge into canonical products, attaching this source as a provider).
  // Was a setInterval over three invented offerings whose "Add selected" button
  // called onClose(), so nothing was ever read or merged.
  function AddOfferingsModal({ site, onClose, onSaved }) {
    const DS = window.HuddleDesignSystem_775113; const { Input, Select } = DS;
    const siteId = siteIdOf(site);
    const [src, setSrc] = useState("site");        // site | competitor | partner | url
    const [entities, setEntities] = useState({ competitor: [], partner: [] });
    const [ownerUrl, setOwnerUrl] = useState("");
    const [pickedId, setPickedId] = useState("");  // chosen competitor/partner id
    const [manualUrl, setManualUrl] = useState("");
    const [phase, setPhase] = useState("pick");    // pick | running | results
    const [run, setRun] = useState(null);
    const [found, setFound] = useState([]);
    const [sel, setSel] = useState([]);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);
    const t = useRef(null);
    useEffect(() => () => { if (typeof t.current === "function") t.current(); }, []);

    // The pickers need real entities and the profile's own page, or "My
    // website" and "A tracked competitor" are choices that cannot resolve.
    useEffect(() => {
      if (siteId == null) return;
      let cancel = false;
      Promise.all([
        api(`/bi/entities?site_id=${siteId}&entity_type=competitor&owner_flag=0`).catch(() => []),
        api(`/bi/entities?site_id=${siteId}&entity_type=partner&owner_flag=0`).catch(() => []),
        api(`/bi/entities?site_id=${siteId}&owner_flag=1`).catch(() => []),
      ]).then(([c, p, o]) => {
        if (cancel) return;
        const withUrl = (rows) => (Array.isArray(rows) ? rows : []).filter(r => r.url);
        setEntities({ competitor: withUrl(c), partner: withUrl(p) });
        const owner = Array.isArray(o) && o[0];
        const d = (owner && owner.data) || {};
        const list = Array.isArray(d.products_urls) ? d.products_urls.filter(Boolean) : [];
        setOwnerUrl(list[0] || d.products_url || (owner && owner.url) || "");
      });
      return () => { cancel = true; };
    }, [siteId]);

    const sources = [["site", "My website", "globe"], ["competitor", "A tracked competitor", "flag"], ["partner", "A tracked partner", "git-branch"], ["url", "Another URL", "link"]];
    const labels = ["Reading the page", "Extracting offerings with AI", "Matching to service lines"];

    // Which page gets read, and who the offerings will be attributed to.
    const picked = (src === "competitor" || src === "partner")
      ? entities[src].find(e => String(e.id) === String(pickedId))
      : null;
    const target = src === "site" ? ownerUrl : src === "url" ? manualUrl.trim() : (picked ? picked.url : "");
    const provider = src === "site"
      ? { kind: "us", name: "Us", url: ownerUrl }
      : src === "url"
        ? { kind: "market", name: manualUrl.trim(), url: manualUrl.trim() }
        : { kind: src, name: picked ? picked.name : "", url: picked ? picked.url : "", ref_entity_id: picked ? picked.id : null, ref_entity_type: src };

    const start = async () => {
      if (!target) {
        setErr(src === "site"
          ? "No products/services page on your Business Profile yet. Add one there, or use Another URL."
          : src === "url" ? "Enter a URL to read." : `Pick a ${src} with a website first.`);
        return;
      }
      if (siteId == null) { setErr("No active site."); return; }
      setPhase("running"); setRun(null); setErr(null);
      const runId = newRunId();
      t.current = pollRun(runId, setRun);
      try {
        const res = await api(`/bi/extract-offerings`, { method: "POST", body: JSON.stringify({ run_id: runId, site_id: siteId, url: target }) });
        t.current();
        const list = ((res && res.offerings) || [])
          .filter(o => o && o.name)
          .map((o, i) => ({ id: i, name: o.name, sum: o.summary || "", price: o.pricing || "", cat: o.category || "" }));
        if (!list.length) { setErr(`No offerings found on ${target}. Try a page that lists what the business sells.`); setPhase("pick"); return; }
        setFound(list); setSel(list.map(o => o.id)); setPhase("results");
      } catch (e) { t.current(); setErr(e.message || "Read failed"); setPhase("pick"); }
    };

    const add = async () => {
      const chosen = found.filter(o => sel.includes(o.id));
      if (!chosen.length || siteId == null) return;
      setSaving(true); setErr(null);
      try {
        const res = await api(`/bi/ingest-offerings`, { method: "POST", body: JSON.stringify({
          site_id: siteId, provider, source_url: target,
          offerings: chosen.map(o => ({ name: o.name, pricing: o.price, category: o.cat })),
        }) });
        // The merge either creates a product or attaches this provider to one
        // that already exists, and those are different outcomes worth naming.
        onSaved && onSaved(res);
        onClose();
      } catch (e) { setErr(e.message || "Add failed"); setSaving(false); }
    };

    const toggle = id => setSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
    const setCat = (id, v) => setFound(fs => fs.map(f => f.id === id ? { ...f, cat: v } : f));

    const foot = phase === "results"
      ? <><button className="bi-btn" onClick={onClose} disabled={saving}>Cancel</button><button className="bi-btn bi-btn--primary" disabled={saving || !sel.length} onClick={add}>{saving ? "Adding…" : `Add selected (${sel.length})`}</button></>
      : <><button className="bi-btn" onClick={onClose}>Cancel</button><button className="bi-btn bi-btn--ai" disabled={phase === "running"} onClick={start}><I name="sparkles" size={16} />Read offerings</button></>;

    return (
      <Modal title="Add offerings from a source" sub="Read offerings from a page and merge them by name + service line." onClose={onClose} foot={foot}>
        <div className="bi-modal__body">
          {err && <div className="bi-hint" style={{ color: "var(--danger)" }}>{err}</div>}
          {phase === "pick" && <div style={{ paddingTop: 12 }}>
            <div className="bi-cf-sources">{sources.map(([id, name, icon]) => <button key={id} className={"bi-cf-source" + (src === id ? " bi-cf-source--on" : "")} onClick={() => { setSrc(id); setErr(null); }}><span className="bi-cf-source__icon"><I name={icon} size={16} /></span><span className="bi-cf-source__name">{name}</span></button>)}</div>
            {(src === "competitor" || src === "partner") && (
              <div style={{ marginTop: 12 }}>
                <label className="bi-field__label">Which {src}</label>
                <Select value={pickedId} onChange={e => setPickedId(e.target.value)}>
                  <option value="">Select a {src}…</option>
                  {entities[src].map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </Select>
                {entities[src].length === 0 && <div className="bi-hint">No tracked {src} has a website yet. Add one with a URL first.</div>}
              </div>
            )}
            {src === "url" && (
              <div style={{ marginTop: 12 }}><label className="bi-field__label">Page URL</label><Input leadingIcon="link" value={manualUrl} onChange={e => setManualUrl(e.target.value)} placeholder="example.com/services" /></div>
            )}
            <div className="bi-hint" style={{ marginTop: 12 }}>
              {target ? <>Reads <b>{target}</b>. Offerings merge into existing products by name, adding this source as a provider.</>
                      : "Pick where to read offerings from."}
            </div>
          </div>}
          {phase === "running" && <FetchProgress run={run} labels={labels} />}
          {phase === "results" && <div style={{ paddingTop: 8 }}>
            <div className="bi-results-head"><I name="check-circle" size={16} style={{ color: "var(--success)" }} />Found {found.length} offering{found.length === 1 ? "" : "s"} · edit service lines before adding</div>
            <div className="bi-proposed-list">{found.map(f => (
              <div className={"bi-proposed-card" + (sel.includes(f.id) ? " bi-proposed-card--on" : "")} key={f.id} onClick={() => toggle(f.id)}>
                <label className="hds-check" onClick={e => e.stopPropagation()}><input type="checkbox" checked={sel.includes(f.id)} onChange={() => toggle(f.id)} /><span className="hds-check__box"><I name="check" size={12} /></span></label>
                <div className="bi-proposed-card__main">
                  <div className="bi-proposed-card__name">{f.name}{f.price && <span className="bi-conf" style={{ color: "var(--text-primary)" }}>{f.price}</span>}</div>
                  {f.sum && <div className="bi-proposed-card__sum">{f.sum}</div>}
                  <div style={{ marginTop: 8 }} onClick={e => e.stopPropagation()}>
                    <Input value={f.cat} onChange={e => setCat(f.id, e.target.value)} placeholder="Service line (optional)" style={{ height: 30, fontSize: 12, maxWidth: 240 }} />
                  </div>
                </div>
              </div>
            ))}</div>
          </div>}
        </div>
      </Modal>
    );
  }
})();
